import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from "../../services/firebase";
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import LoadingSpinner from '../LoadingSpinner';
import { ReactComponent as ClientesIcon } from '../../assets/icons/ClientesIcon.svg';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { useCotizaciones, aPesos } from '../../services/cotizaciones';
// Símbolo real de cada venta según su tipoDivisa — antes estas tablas mostraban
// "$" fijo aunque la venta estuviera cargada en Dólares/Reales/Euros.
const SIMBOLOS_DIVISA = { Pesos: "$", "Dólares": "US$", Reales: "R$", Euros: "€" };
const opcionesPeriodo = [
{ id: "semanal", label: "Monto total semanal" },
{ id: "mensual", label: "Monto total mensual" },
{ id: "trimestral", label: "Monto total trimestral" },
{ id: "semestral", label: "Monto total semestral" },
{ id: "anual", label: "Monto total anual" },
{ id: "historico", label: "Monto total histórico" },
];
const ClienteDetails = () => {
  const { id } = useParams();
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [compras, setCompras] = useState([]);
  const [periodo, setPeriodo] = useState(opcionesPeriodo[5]);
  const [montoSeleccionado, setMontoSeleccionado] = useState(0);
  const [activeTab, setActiveTab] = useState('enFinca');
  // El monto total del período mezcla ventas en distintas divisas: se pasan
  // todas a Pesos antes de sumar (antes se sumaba totalFinal crudo).
  const { cotizaciones } = useCotizaciones();
  const calcularMonto = (ventas, periodo) => {
    const ahora = new Date();
    let fechaInicio;
const ventasSinSena = ventas.filter(venta => 
!venta.productosVendidos?.some(prod => prod.categoria === 'seña')
);
    switch (periodo.id) {
      case "semanal":
        fechaInicio = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate() - 7);
        break;
      case "mensual":
        fechaInicio = new Date(ahora.getFullYear(), ahora.getMonth() - 1, ahora.getDate());
        break;
      case "trimestral":
        fechaInicio = new Date(ahora.getFullYear(), ahora.getMonth() - 3, ahora.getDate());
        break;
      case "semestral":
        fechaInicio = new Date(ahora.getFullYear(), ahora.getMonth() - 6, ahora.getDate());
        break;
      case "anual":
        fechaInicio = new Date(ahora.getFullYear() - 1, ahora.getMonth(), ahora.getDate());
        break;
      case "historico":
      default:
        fechaInicio = null;
        break;
    }
    return ventasSinSena.reduce((total, venta) => {
      const fechaVenta = venta.fechaVenta?.toDate ? venta.fechaVenta.toDate() : new Date(venta.fechaVenta);
      if (!fechaInicio || fechaVenta >= fechaInicio) {
        return total + aPesos(venta.totalFinal, venta.tipoDivisa, cotizaciones);
      }
      return total;
    }, 0);
  };
  useEffect(() => {
    const fetchCliente = async () => {
      if (!id) {
        setError('ID de cliente no encontrado.');
        setLoading(false);
        return;
      }
      try {
        const docRef = doc(db, 'clientes', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const clienteData = { id: docSnap.id, ...docSnap.data() };
          setCliente(clienteData);
          if (clienteData.historialDeCompras?.length) {
            const ventasPromises = clienteData.historialDeCompras.map(ventaId =>
              getDoc(doc(db, 'ventas', ventaId))
            );
            const ventasSnapshots = await Promise.all(ventasPromises);
const ventasData = ventasSnapshots
  .filter(snap => snap.exists())
  .map(snap => ({ id: snap.id, ...snap.data() }))
  .sort((a, b) => {
    const fechaA = a.fechaVenta?.toDate ? a.fechaVenta.toDate() : new Date(a.fechaVenta);
    const fechaB = b.fechaVenta?.toDate ? b.fechaVenta.toDate() : new Date(b.fechaVenta);
    return fechaB - fechaA; // más reciente primero
  });

setCompras(ventasData);

          }
        } else {
          setError('Cliente no encontrado.');
        }
      } catch (err) {
        console.error('Error al obtener el cliente:', err);
        setError('Hubo un error al cargar los datos del cliente.');
      } finally {
        setLoading(false);
      }
    };
    fetchCliente();
  }, [id]);
  useEffect(() => {
    if (compras.length > 0) {
      setMontoSeleccionado(calcularMonto(compras, periodo));
    }
  }, [compras, periodo, cotizaciones]);
  if (loading) return <LoadingSpinner />;
  if (error) return <div className="error-message">{error}</div>;
  if (!cliente) return <div>No se encontró el cliente.</div>;
    const formatearFecha = (fecha) => {
        if (!fecha) return "-";
        const date = fecha.toDate ? fecha.toDate() : new Date(fecha);
        return date.toLocaleDateString("es-AR");
    };
    const formatearTimestamp = (fecha) => {
        if (!fecha) return "-";
        const date = fecha.toDate ? fecha.toDate() : new Date(fecha);
        const fechaString = date.toLocaleDateString("es-AR", { day: '2-digit', month: '2-digit', year: 'numeric' });
        const horaString = date.toLocaleTimeString("es-AR", { hour: '2-digit', minute: '2-digit', hour12: false });
        return `El ${fechaString} a las ${horaString}`;
    };
    const formatearDomicilio = (domicilio) => {
        const { calle, altura, piso, unidad } = domicilio || {};
        let direccion = '';
        if (calle) direccion += `${calle}`;
        if (altura) direccion += ` ${altura}`;
        if (piso && unidad) direccion += `, ${piso}° ${unidad}`;
        else if (piso === 'PB') direccion += `, ${piso}`;
        else if (piso) direccion += `, ${piso}°`;
        else if (unidad) direccion += `, Dpto. ${unidad}`;
        return direccion || "No especificado";
    };
    const tipoDocumentoAbreviado = {
        'DNI': 'DNI',
        'Libreta Cívica': 'LC',
        'Pasaporte': 'P'
    };
const comprasEventos = compras.filter(
  (venta) => venta.productosVendidos?.some(prod => prod.categoria === "eventos")
);
const comprasEnFinca = compras
  .filter((venta) => !venta.productosVendidos?.some(prod => prod.categoria === "eventos"))
  .map((venta) => ({
    ...venta,
    productosVendidos: venta.productosVendidos.filter(prod => prod.categoria !== "seña")
  }))
  .filter((venta) => venta.productosVendidos.length > 0);
return (
<div className="doc-detail viticultura-detail" style={{padding:'0px 16px'}}>
<header className="trazabilidad-header">
<div className="header-content">
<h1>Clientes</h1>
</div>
</header>
<div className="doc-detail header" style={{ display: 'flex', gap: '40px' }}>
<div className="header-column" style={{ flex: 1, flexDirection: 'row', gap: '10px', justifyContent: 'left', alignItems:'center' }}>
<div className="doc-icon-container">
<ClientesIcon className="doc-icon" style={{ width:'20px', margin:'auto' }}/>
</div>
<p className="header-value" style={{fontSize: '22px', color: '#2c2c2c'}}>
{cliente.personeria === 'persona fisica'
? `${(cliente.apellido || '').toUpperCase()}, ${cliente.nombre || ''}`
: cliente.razonSocial || ''}
</p>
</div>
<div className="header-column" style={{ flex: 1 }}>
<label className="header-label">Creado por</label>
<p className="header-value">{cliente.creador || 'Finca Los Astros'}</p>
<p className="header-value">{formatearTimestamp(cliente.fechaCreacion)}</p>
</div>
<div className="header-column" style={{ flex: 1 }}>
<label className="header-label">Última modificación</label>
<p className="header-value">{cliente.modificador || '-'}</p>
<p className="header-value">{cliente.ultimaActualizacion ? formatearTimestamp(cliente.ultimaActualizacion) : '-'}</p>
</div>
</div>
<div className="columns-container" style={{ display: 'flex', gap: '40px' }}>
<div className="data-column">
{cliente.personeria === 'persona fisica' && (
<>
<label className="data-label">Fecha de Nacimiento</label>
<p className="data-p">{cliente.fechaDeNacimiento ? formatearFecha(cliente.fechaDeNacimiento) : '-'}</p>
</>
)}
<label className="data-label">{cliente.personeria === 'persona fisica' ? 'Documento' : 'CUIT'}</label>
<p className="data-p">
{cliente.personeria === 'persona fisica'
? `${tipoDocumentoAbreviado[cliente.tipo] || ''} ${cliente.numeroDeDocumento || '-'}`
: cliente.cuit || '-'}
</p>
</div>
<div className="data-column">
<label className="data-label">Número de teléfono</label>
<p className="data-p">{cliente.telefono || '-'}</p>
<label className="data-label">Correo Electrónico</label>
<p className="data-p">{cliente.correoElectronico || '-'}</p>
{cliente.personaDeContacto && (
<>
<label className="data-label">Persona de Contacto</label>
<p className="data-p">{cliente.personaDeContacto}</p>
</>
)}
</div>
<div className="data-column">
<label className="data-label">Domicilio</label>
<p className="data-p">
{formatearDomicilio(cliente)}<br/>{cliente.codigoPostal}<br/>{cliente.ciudad}, {cliente.pais}
</p>
</div>
</div>
{(compras.length > 0 || cliente.eventos?.length > 0) && (
<>
<div className="historial-header-row" style={{padding:'0 11px'}}>
<h3>Historial de compras</h3>
<div className="monto-historico">
<div className="input-group">
<Listbox value={periodo} onChange={setPeriodo}>
{({ open }) => (
<>
<ListboxButton
className={`monto-listbox-button ${open ? 'open' : ''}`}
style={{ textAlign: 'center' }}
>
{periodo.label}
<span className="monto-arrow-container">
<span className="monto-arrow"></span>
</span>
</ListboxButton>
<ListboxOptions className="monto-listbox-options">
{opcionesPeriodo.map((op) => (
<ListboxOption
key={op.id}
value={op}
className="monto-listbox-option"
>
{op.label}
</ListboxOption>
))}
</ListboxOptions>
</>
)}
</Listbox>
</div>
<p style={{ margin: "0" }}>
  ${Math.floor(montoSeleccionado).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
</p>
</div>
</div>
<div className="section" style={{ padding: '0 16px' }}>
{}
<ul className="clientes-tabs">
<li className={activeTab === "enFinca" ? "active" : ""}>
<a href="#" onClick={(e) => { e.preventDefault(); setActiveTab("enFinca"); }}>
En Finca
</a>
</li>
{comprasEventos.length > 0 && (
<li className={activeTab === "eventos" ? "active" : ""}>
<a href="#" onClick={(e) => { e.preventDefault(); setActiveTab("eventos"); }}>
Eventos
</a>
</li>
)}
</ul>
{activeTab === "enFinca" && (
<div className="tab-content">
{comprasEnFinca.length > 0 ? (
<table className="historial-table">
<thead>
<tr>
<th>Fecha de compra</th>
<th>Detalles</th>
<th>Monto total</th>
<th>Modalidad de pago</th>
</tr>
</thead>
<tbody>
{comprasEnFinca.map((venta) => (
<tr key={venta.id}>
<td>{formatearFecha(venta.fechaVenta)}</td>
<td>
{venta.productosVendidos.map((prod, index) => (
<div key={index}>
{prod.nombreProducto} x {prod.cantidad}
</div>
))}
</td>
<td>{SIMBOLOS_DIVISA[venta.tipoDivisa] || "$"}{Math.floor(venta.totalFinal || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
<td>{venta.modalidadPago}</td>
</tr>
))}
</tbody>
</table>
) : (
<p>No hay compras registradas en la finca.</p>
)}
</div>
)}
{activeTab === "eventos" && (
<div className="tab-content">
{comprasEventos.length > 0 ? (
<table className="historial-table">
<thead>
<tr>
<th>Fecha del evento</th>
<th>Descripción</th>
<th>Monto total</th>
</tr>
</thead>
<tbody>
{comprasEventos.map((venta) => (
<tr key={venta.id}>
<td>{formatearFecha(venta.fechaVenta)}</td>
<td>
{venta.productosVendidos.map((prod, index) => (
<div key={index}>
{prod.nombreProducto}
</div>
))}
</td>
<td>{SIMBOLOS_DIVISA[venta.tipoDivisa] || "$"}{Math.floor(venta.totalFinal || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
</tr>
))}
</tbody>
</table>
) : (
<p>No hay eventos registrados.</p>
)}
</div>
)}
</div>
</>
)}
</div>
);
};
export default ClienteDetails;